import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import filterTree from '../../../utils/linkedTree/filterTree';
import EditSessionProvider from '../../containers/EditSessionProvider';

import Tree from '../NextTree';
import Expansion from '../NextTree/plugins/Expansion';
import ContentBox from '../../utils/ContentBox';
import TreeContentWrapper from '../BasicTrees/TreeContentWrapper';
import { treeLayoutProps } from '../BasicTrees/TreeLayoutConfig';
import Empty from '../../utils/Empty';

const SEARCH_QUERY_FIELD_NAME = 'searchQuery';

/**
 * Filters selection to only include nodes that exist in the given models array
 */
function filterSelectionByModels(selection, models, adapter) {
  if (!selection || !models) {
    return selection;
  }

  const modelIds = new Set(models.map(model => adapter.nodeId(model)));

  if (Array.isArray(selection)) {
    return selection.filter(node => modelIds.has(adapter.nodeId(node)));
  }

  return modelIds.has(adapter.nodeId(selection)) ? selection : null;
}

/**
 * Merges new selection with existing selection, preserving nodes not in current models
 */
function mergeSelectionWithHidden(
  newSelection,
  fullSelection,
  currentModels,
  adapter,
) {
  if (!Array.isArray(fullSelection)) {
    return newSelection;
  }

  if (!fullSelection) {
    return newSelection;
  }

  const currentModelIds = new Set(
    currentModels.map(model => adapter.nodeId(model)),
  );

  // Get nodes from full selection that are not in current models (hidden nodes)
  const hiddenSelection = fullSelection.filter(
    node => !currentModelIds.has(adapter.nodeId(node)),
  );

  // Merge new selection with hidden selection
  if (Array.isArray(newSelection)) {
    return [...newSelection, ...hiddenSelection];
  }

  if (newSelection) {
    return [newSelection, ...hiddenSelection];
  }

  return hiddenSelection.length > 0 ? hiddenSelection : null;
}

export default class StaticTreeStaticBodyContent extends React.PureComponent {
  static makeSearchTreeId(mainTreeId, query) {
    return `${mainTreeId}::Search::${query}`;
  }

  static propTypes = {
    tree: PropTypes.string.isRequired,
    models: PropTypes.array.isRequired,
    rootModels: PropTypes.array,
    adapter: PropTypes.shape({
      nodeId: PropTypes.func.isRequired,
      nodeParentId: PropTypes.func.isRequired,
      nodeTitle: PropTypes.func.isRequired,
      nodeIsLeaf: PropTypes.func.isRequired,
      renderNodeAddons: PropTypes.func.isRequired,
    }).isRequired,
    nodeShouldBeInitiallyVisible: PropTypes.func,
    inflateInitialExpansion: PropTypes.bool,
    isSyntheticRootNodeAlwaysVisible: PropTypes.bool,
    plugins: PropTypes.array.isRequired,
    filter: PropTypes.shape({
      value: PropTypes.object.isRequired,
      predicatesMeta: PropTypes.arrayOf(
        PropTypes.shape({
          valuePropName: PropTypes.string.isRequired,
          predicate: PropTypes.func.isRequired,
          includeChildren: PropTypes.bool,
          collectVisibleIds: PropTypes.bool,
        }),
      ),
    }),
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    selection: PropTypes.any,
    onSelectionChange: PropTypes.func,
    setMainTreeModels: PropTypes.func,
    dataCy: PropTypes.string,
    ...treeLayoutProps,
  };

  static defaultProps = {
    children: null,
    selection: null,
    onSelectionChange: null,
    inflateInitialExpansion: true,
    nodeShouldBeInitiallyVisible: null,
    isSyntheticRootNodeAlwaysVisible: true,
    rootModels: [],
    filter: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      models: [],
      searchTreeModels: null,
      mainTreeModels: null,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { models, filter, adapter, setMainTreeModels } = nextProps;

    const {
      models: prevModels,
      filterValue,
      searchTreeModels,
      mainTreeModels,
    } = prevState;

    const buildTreeAdapter = {
      getNodeId: adapter.nodeId,
      getNodeParentId: adapter.nodeParentId,
    };

    const isModelsEquals = _.isEqual(models, prevModels);

    if (!filter) {
      if (!isModelsEquals) {
        const mainTreeNewModels = filterTree(
          models,
          [
            {
              includeChildren: true,
              collectVisibleIds: true,
              predicate: () => true,
            },
          ],
          buildTreeAdapter,
          filterValue,
        );

        setMainTreeModels &&
          setMainTreeModels(_.get(mainTreeNewModels, 'entities'));

        return {
          models,
          mainTreeModels: mainTreeNewModels,
        };
      }

      return null;
    }

    const { value, predicatesMeta } = filter;

    const omittedFilter = _.omit(value, SEARCH_QUERY_FIELD_NAME);

    const rebuildMainTree =
      !isModelsEquals ||
      !_.isEqual(_.omit(filterValue, SEARCH_QUERY_FIELD_NAME), omittedFilter);
    const rebuildSearchTree = !isModelsEquals || !_.isEqual(filterValue, value);

    if (!rebuildMainTree && !rebuildSearchTree) {
      return null;
    }

    const newState = {
      models,
      filterValue: value,
      mainTreeModels: rebuildMainTree
        ? buildMainTree(models, omittedFilter, predicatesMeta, buildTreeAdapter)
        : mainTreeModels,
      searchTreeModels: rebuildSearchTree
        ? buildSearchTree(models, value, predicatesMeta, buildTreeAdapter)
        : searchTreeModels,
    };

    setMainTreeModels &&
      setMainTreeModels(_.get(newState, 'mainTreeModels.entities'));

    return newState;
  }

  renderChildren() {
    const {
      children,
      dataCy,
      editNodeComponent,
      paragraph,
      treeLayoutType,
    } = this.props;
    const { searchTreeModels } = this.state;

    const Body =
      searchTreeModels && _.isEmpty(searchTreeModels?.entities) ? (
        <Empty message="There is no data matching your criteria" />
      ) : (
        <Tree.Body />
      );

    const content = (
      <TreeContentWrapper
        EditComponent={editNodeComponent}
        isNodeEditVisible={!!editNodeComponent}
        treeLayoutType={treeLayoutType}
      >
        <ContentBox
          hasLimitScrollableContentHeight
          isScrolled
          className="tree-content-box"
          dataCy={dataCy}
          paragraph={paragraph}
        >
          {Body}
        </ContentBox>
      </TreeContentWrapper>
    );

    if (children) {
      if (typeof children === 'function') {
        return children(content);
      }
      return children;
    }

    return content;
  }

  render() {
    const {
      tree,
      selection,
      onSelectionChange,
      handleClear,
      filter,
      adapter,
      plugins = {},
      nodeShouldBeInitiallyVisible,
      inflateInitialExpansion,
      isSyntheticRootNodeAlwaysVisible,
      rootModels,
      setMainTreeChildrenCounts,
      getMainTreeChildrenCounts,
    } = this.props;

    const { searchTreeModels, mainTreeModels } = this.state;

    const searchEntities = _.get(searchTreeModels, 'entities');
    const mainEntities = _.get(mainTreeModels, 'entities');

    const searchModel =
      (!_.isEmpty(searchEntities) && searchEntities) ||
      (isSyntheticRootNodeAlwaysVisible && !_.isEmpty(rootModels)
        ? rootModels
        : []);

    const mainModel =
      (!_.isEmpty(mainEntities) && mainEntities) ||
      (isSyntheticRootNodeAlwaysVisible && !_.isEmpty(rootModels)
        ? rootModels
        : []);

    const mainTreePlugins = [
      ...plugins,
      new Expansion({
        inflateInitialExpansion,
        nodeShouldBeInitiallyVisible: node => {
          const {
            mainTreeModels: { visibleEntityIds },
          } = this.state;

          return nodeShouldBeInitiallyVisible
            ? nodeShouldBeInitiallyVisible(node)
            : visibleEntityIds.has(adapter.nodeId(node));
        },
      }),
    ];

    const searchTreePlugins = [
      ...plugins,
      new Expansion({
        inflateInitialExpansion,
        nodeShouldBeInitiallyVisible: node => {
          const {
            searchTreeModels: { visibleEntityIds },
          } = this.state;
          return nodeShouldBeInitiallyVisible
            ? nodeShouldBeInitiallyVisible(node)
            : visibleEntityIds.has(adapter.nodeId(node));
        },
      }),
    ];

    // Filter selection for search tree to only include nodes that exist in search results
    const searchTreeSelection = searchTreeModels
      ? filterSelectionByModels(selection, searchModel, adapter)
      : selection;

    // Create selection change handlers that preserve hidden selections
    const handleMainTreeSelectionChange = newSelection => {
      // For main tree, use the selection as-is since it contains all nodes
      onSelectionChange(newSelection);
    };

    const handleSearchTreeSelectionChange = newSelection => {
      // For search tree, merge with hidden selections (nodes not in search results)
      const mergedSelection = mergeSelectionWithHidden(
        newSelection,
        selection,
        searchModel,
        adapter,
      );
      onSelectionChange(mergedSelection);
    };

    return (
      <EditSessionProvider>
        {isEditSessionActive => (
          <Tree
            adapter={adapter}
            context={{
              isEditSessionActive,
              setMainTreeChildrenCounts,
              getMainTreeChildrenCounts,
            }}
            handleClear={handleClear}
            models={mainModel}
            plugins={mainTreePlugins}
            selection={selection}
            tree={tree}
            onSelectionChange={handleMainTreeSelectionChange}
          >
            {searchTreeModels ? (
              <Tree
                key={StaticTreeStaticBodyContent.makeSearchTreeId(
                  tree,
                  filter.value[SEARCH_QUERY_FIELD_NAME],
                )}
                adapter={adapter}
                context={{
                  isEditSessionActive,
                  getMainTreeChildrenCounts,
                }}
                handleClear={handleClear}
                models={searchModel}
                plugins={searchTreePlugins}
                selection={selection}
                tree={StaticTreeStaticBodyContent.makeSearchTreeId(
                  tree,
                  filter.value[SEARCH_QUERY_FIELD_NAME],
                )}
                onSelectionChange={handleSearchTreeSelectionChange}
              >
                {this.renderChildren()}
              </Tree>
            ) : (
              this.renderChildren()
            )}
          </Tree>
        )}
      </EditSessionProvider>
    );
  }
}

function isModelsEmpty(models) {
  return (
    models &&
    ((Array.isArray(models) && models.length === 0) ||
      (models.entities && models.entities.length === 0))
  );
}

function buildMainTree(models, filter, predicatesMeta, buildTreeAdapter) {
  const predicates = predicatesMeta.reduce(
    (accumulator, { predicate, valuePropName, ...rest }) => {
      if (!filter[valuePropName]) {
        return accumulator;
      }

      accumulator.push({
        predicate: predicate(filter[valuePropName]),
        ...rest,
      });

      return accumulator;
    },
    [],
  );

  return filterTree(models, predicates, buildTreeAdapter, filter);
}

function buildSearchTree(models, filter, predicatesMeta, buildTreeAdapter) {
  if (!filter || _.isEmpty(filter[SEARCH_QUERY_FIELD_NAME])) {
    return null;
  }

  const predicates = predicatesMeta.reduce(
    (accumulator, { predicate, valuePropName, ...rest }) => {
      if (!filter[valuePropName]) {
        return accumulator;
      }

      accumulator.push({
        predicate: predicate(filter[valuePropName]),
        ...rest,
      });

      return accumulator;
    },
    [],
  );

  return filterTree(models, predicates, buildTreeAdapter, filter);
}
